/**
 * useDirectoryNames — id → human name resolution for display surfaces.
 *
 * The problem this solves: tables, drawers and cards across the app fell back
 * to raw UUIDs (customerId, vehicleId, consultantId, leadId) because they
 * relied on the API echoing `*Name` fields that real rows don't carry. The
 * lookup hooks that DO resolve names existed but were only used inside
 * create/edit modals.
 *
 * This composes those hooks once and exposes resolver functions. Each takes
 * an optional `fallbackName` first (a name the row may already carry, e.g.
 * `appt.customerName`) so callers can prefer a server-provided name and only
 * resolve when it's missing:
 *
 *     const names = useDirectoryNames();
 *     names.customer(row.customerId, row.customerName)   // name, resolved, or —
 *     names.vehicle(row.vehicleId, row.vehicleLabel)
 *     names.consultant(row.consultantId, row.consultantName)
 *     names.lead(row.leadId)
 *
 * When nothing resolves it returns a friendly em dash (or a short #id only as
 * an absolute last resort, controlled per-call).
 *
 * All four directories are small (≤200 rows for a single dealership), so
 * loading them together is cheap. If a page only needs one, prefer the
 * specific hook (useCustomerOptions, etc.) to avoid the extra fetches.
 */
import { useMemo } from 'react';
import {
  useConsultantOptions, useCustomerOptions, useVehicleOptions, useLeadOptions,
} from './useDirectory';

const shortId = (id) => (id ? `#${String(id).slice(0, 8)}` : null);

export function useDirectoryNames() {
  const consultants = useConsultantOptions();
  const customers   = useCustomerOptions();
  const vehicles    = useVehicleOptions();

  const customerMap = useMemo(
    () => new Map(customers.options.map(o => [o.id, o])),
    [customers.options],
  );
  const leads = useLeadOptions(customerMap);

  const consultantMap = useMemo(
    () => new Map(consultants.options.map(o => [o.id, o])),
    [consultants.options],
  );
  const vehicleMap = useMemo(
    () => new Map(vehicles.options.map(o => [o.id, o])),
    [vehicles.options],
  );
  const leadMap = useMemo(
    () => new Map(leads.options.map(o => [o.id, o])),
    [leads.options],
  );

  return useMemo(() => {
    /**
     * @param map        id → option ({ primary, secondary })
     * @param id         entity id
     * @param fallback   name the row may already carry (preferred if truthy)
     * @param opts       { showId: show a #shortId when unresolved; dash: text when nothing }
     */
    const resolve = (map, id, fallback, opts = {}) => {
      if (fallback) return fallback;
      const hit = id ? map.get(id) : null;
      if (hit?.primary) return hit.primary;
      if (opts.showId && id) return shortId(id);
      return opts.dash ?? '—';
    };

    return {
      loading: consultants.loading || customers.loading || vehicles.loading || leads.loading,

      consultant: (id, fallback, opts) => resolve(consultantMap, id, fallback, opts),
      customer:   (id, fallback, opts) => resolve(customerMap, id, fallback, opts),
      vehicle:    (id, fallback, opts) => resolve(vehicleMap, id, fallback, opts),
      lead:       (id, fallback, opts) => resolve(leadMap, id, fallback, opts),

      // Full option objects when a caller needs the secondary line too.
      consultantOption: (id) => (id ? consultantMap.get(id) || null : null),
      customerOption:   (id) => (id ? customerMap.get(id) || null : null),
      vehicleOption:    (id) => (id ? vehicleMap.get(id) || null : null),
      leadOption:       (id) => (id ? leadMap.get(id) || null : null),

      maps: { consultantMap, customerMap, vehicleMap, leadMap },
    };
  }, [
    consultants.loading, customers.loading, vehicles.loading, leads.loading,
    consultantMap, customerMap, vehicleMap, leadMap,
  ]);
}

export default useDirectoryNames;
